import React, { useMemo } from 'react';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../../../hooks/hooks';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../../../components/shared/table-v2/types';
import { TiminatorTagType } from '../../../../../features/types/types';
import { Lx } from '../../../../../i18n/consts';
import { StatusTag } from '../../../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../../../components/shared/delete-icon-button';
import { StyledMobileDefaultText } from '../../../../../components/shared/styled-mobile-default-text';
import { normalizeString } from '../../../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusSortValue, getStatusValue } from '../../../../../utils/table-filters';

export const useTagTypesTable = (
    search: string,
    selectedStatus: number,
    isMobile: boolean
): TiminatorTableSettings<TiminatorTagType> => {
    const intl = useIntl();
    const tagTypes = useAppSelector(store => store.types.tags.items);
    const defaultLabel = intl.formatMessage({ id: Lx.General.DEFAULT });

    const columns = useMemo((): TiminatorTableColumn<TiminatorTagType>[] => {
        const baseColumns: TiminatorTableColumn<TiminatorTagType>[] = [];
        const getTypeSortValue = (rowData: TiminatorTagType) => (rowData.default ? 1 : 0);

        if (isMobile) {
            baseColumns.push({
                id: 'name',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                sortable: true,
                getSortValue: (rowData: TiminatorTagType) => normalizeString(rowData.name),
                format: (rowData: TiminatorTagType) => {
                    return (
                        <span>
                            <div>{rowData.name}</div>
                            {rowData.default && (
                                <StyledMobileDefaultText text={intl.formatMessage({ id: Lx.General.DEFAULT })} />
                            )}
                        </span>
                    );
                },
            });
        } else {
            baseColumns.push(
                {
                    id: 'name',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    sortable: true,
                    getSortValue: (rowData: TiminatorTagType) => normalizeString(rowData.name),
                    format: (rowData: TiminatorTagType) => rowData.name,
                },
                {
                    id: 'default',
                    label: intl.formatMessage({ id: Lx.General.TYPE }),
                    sortable: true,
                    getSortValue: getTypeSortValue,
                    format: (rowData: TiminatorTagType) => {
                        return (
                            <span>
                                {rowData.default && (
                                    <div>{intl.formatMessage({ id: Lx.General.DEFAULT })}</div>
                                )}
                            </span>
                        );
                    },
                }
            );
        }

        baseColumns.push(
            {
                id: 'status',
                label: 'Status',
                align: isMobile ? 'right' : 'left',
                sortable: true,
                getSortValue: (rowData: TiminatorTagType) => getStatusSortValue(rowData.active),
                format: (rowData: TiminatorTagType) => {
                    return (
                        <StatusTag status={rowData.active ? 'active' : 'suspended'}>
                            <span>
                                {rowData.active ? <FormattedMessage id="active" /> : <FormattedMessage id="suspended" />}
                            </span>
                        </StatusTag>
                    );
                },
                width: isMobile ? '30%' : undefined,
            },
            {
                id: 'deleteButton',
                format: (rowData: TiminatorTagType) => {
                    return <DeleteIconButton />;
                },
                width: isMobile ? '24px' : '64px',
            }
        )

        return baseColumns;
    }, [isMobile, intl]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            tagTypes,
            selectedStatus,
            tag => tag.active,
            search,
            tag => buildSearchValues([
                tag.name,
                tag.default ? defaultLabel : '',
                getStatusValue(intl, tag.active),
            ])
        )
    ), [tagTypes, search, selectedStatus, defaultLabel, intl]);

    const isEmpty = tagTypes.length === 0;

    return {
        columns,
        data,
        isEmpty,
    };
};
